/**
 * Landing Page AI & Strategy Builder Section
 * ZO Bull Algo Trading Platform
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const AI_FEATURES = [
  {
    icon: '💬',
    titleKey: 'aiFeatureNLTitle',
    descKey: 'aiFeatureNLDesc',
    exampleKey: 'aiFeatureNLExample'
  },
  {
    icon: '📊',
    titleKey: 'aiFeatureRealtimeTitle',
    descKey: 'aiFeatureRealtimeDesc',
    exampleKey: 'aiFeatureRealtimeExample'
  },
  {
    icon: '🤖',
    titleKey: 'aiFeatureBacktestTitle',
    descKey: 'aiFeatureBacktestDesc',
    exampleKey: 'aiFeatureBacktestExample'
  }
]

const BUILDER_FEATURES = [
  { icon: '📈', labelKey: '274indicators' },
  { icon: '🔢', labelKey: '163operators' },
  { icon: '📊', labelKey: 'candlestickPatterns' },
  { icon: '📉', labelKey: 'volumeAnalysis' },
  { icon: '📐', labelKey: 'multiTimeframe' },
  { icon: '🔗', labelKey: 'logicalConditions' },
  { icon: '📝', labelKey: 'pineScript' },
  { icon: '🕯️', labelKey: '60patterns' },
  { icon: '📐', labelKey: 'optionsGreeks' },
]

export default function LandingAISection() {
  const { t } = useTranslation();
  
  return (
    <section className="landing-ai-section">
      <div className="section-container">
        <div className="ai-content">
          <div className="ai-text">
            <span className="section-badge">🤖 {t('landing.aiPoweredBadge')}</span>
            <h2 className="section-title">{t('landing.aiSectionTitle')}</h2>
            <p className="section-description">
              {t('landing.aiSectionDesc')}
            </p>
            
            <div className="ai-features">
              {AI_FEATURES.map((feature, index) => (
                <div key={index} className="ai-feature-card">
                  <div className="ai-feature-header">
                    <span className="ai-feature-icon">{feature.icon}</span>
                    <h4>{t(`landing.${feature.titleKey}`)}</h4>
                  </div>
                  <p className="ai-feature-desc">{t(`landing.${feature.descKey}`)}</p>
                  <div className="ai-feature-example">
                    <span className="example-label">{t('landing.trySaying')}:</span>
                    <span className="example-text">"{t(`landing.${feature.exampleKey}`)}"</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="ai-visual">
            <div className="ai-copilot-preview">
              <div className="copilot-header">
                <span className="copilot-icon">🤖</span>
                <span className="copilot-title">{t('landing.aiCopilot')}</span>
                <span className="copilot-status">{t('landing.online')}</span>
              </div>
              <div className="copilot-messages">
                <div className="message user-message">
                  <span className="message-text">{t('landing.aiDemoQuestion')}</span>
                </div>
                <div className="message ai-message">
                  <span className="message-text" dangerouslySetInnerHTML={{ __html: t('landing.aiDemoResponse') }} />
                  <div className="message-actions">
                    <button className="msg-action">📊 {t('landing.backtest')}</button>
                    <button className="msg-action">🚀 {t('landing.deploy')}</button>
                    <button className="msg-action">✏️ {t('landing.edit')}</button>
                  </div>
                </div>
              </div>
              <div className="copilot-input">
                <input type="text" placeholder={t('landing.aiInputPlaceholder')} />
                <button className="btn btn-primary">{t('landing.send')}</button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="builder-content">
          <div className="builder-visual">
            <div className="builder-preview">
              <div className="builder-header">
                <span className="builder-icon">📊</span>
                <span className="builder-title">{t('landing.strategyBuilder')}</span>
              </div>
              <div className="builder-conditions">
                <div className="condition-group">
                  <label>{t('landing.entryCondition')}</label>
                  <div className="condition-row">
                    <span className="indicator">RSI(14)</span>
                    <span className="operator">&lt;</span>
                    <span className="value">30</span>
                  </div>
                  <div className="condition-row">
                    <span className="connector">AND</span>
                  </div>
                  <div className="condition-row">
                    <span className="indicator">SMA(50)</span>
                    <span className="operator">&gt;</span>
                    <span className="value">SMA(200)</span>
                  </div>
                </div>
                <div className="condition-group">
                  <label>{t('landing.exitCondition')}</label>
                  <div className="condition-row">
                    <span className="indicator">RSI(14)</span>
                    <span className="operator">&gt;</span>
                    <span className="value">70</span>
                  </div>
                </div>
                <div className="condition-group">
                  <label>{t('landing.riskManagement')}</label>
                  <div className="condition-row">
                    <span className="risk-item">{t('landing.stopLoss')}</span>
                    <span className="risk-item">{t('landing.takeProfit')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="builder-text">
            <span className="section-badge">🔧 {t('landing.visualBuilderBadge')}</span>
            <h2 className="section-title">{t('landing.builderSectionTitle')}</h2>
            <p className="section-description">
              {t('landing.builderSectionDesc')}
            </p>
            
            <div className="builder-features">
              {BUILDER_FEATURES.map((feature, index) => (
                <div key={index} className="builder-feature">
                  <span className="builder-feature-icon">{feature.icon}</span>
                  <span className="builder-feature-label">{t(`landing.${feature.labelKey}`)}</span>
                </div>
              ))}
            </div>
            
            <div className="builder-cta">
              <Link to="/login" className="btn btn-primary btn-lg">
                {t('landing.tryStrategyBuilder')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
